import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PropertyFields from "../components/site/PropertyFields";
import { toast } from "sonner";
import { Camera, X, Loader2, CheckCircle2 } from "lucide-react";
import { http, apiError, imgUrl } from "../lib/api";
import { CATEGORIES, CATEGORY_INFO } from "../lib/categories";
import LocationPicker from "../components/site/LocationPicker";
import PlanStatus from "../components/site/PlanStatus";
import { useAuth } from "../lib/auth";

const EMPTY = { category: "", title: "", description: "", price: "", mobile: "", location: "", taluka: "", place: "", condition: "", property_type: "", area: "", area_unit: "", has_712: null, direct_owner: null };
const NO_CONDITION = ["Jobs", "Services", "Property"];

function PhotoPicker({ files, setFiles, existing = [], setExisting }) {
  const ref = useRef();
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  const room = 5 - existing.length;
  const add = (list) => {
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/"));
    const big = imgs.find((f) => f.size > 6 * 1024 * 1024);
    if (big) toast.error("प्रत्येक फोटो 6MB पेक्षा कमी असावा");
    setFiles((prev) => [...prev, ...imgs.filter((f) => f.size <= 6 * 1024 * 1024)].slice(0, room));
  };
  const total = existing.length + files.length;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
      {existing.map((p, i) => (
        <div key={p} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-existing-${i}`}>
          <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
          {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-[var(--navy)]/80 text-white text-[10px] text-center py-0.5">COVER</span>}
          <button type="button" onClick={() => setExisting(existing.filter((x) => x !== p))} className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow" data-testid={`photo-existing-remove-${i}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
      {files.map((f, i) => (
        <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`photo-preview-${i}`}>
          <img src={urls[i]} alt="" className="w-full h-full object-cover" />
          {i === 0 && existing.length === 0 && <span className="absolute bottom-0 inset-x-0 bg-[var(--navy)]/80 text-white text-[10px] text-center py-0.5">COVER</span>}
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-white rounded-full p-0.5 shadow" data-testid={`photo-remove-${i}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
      {total < 5 && (
        <button type="button" onClick={() => ref.current.click()} className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-[var(--orange)] hover:bg-orange-50 flex flex-col items-center justify-center text-slate-500 text-xs gap-1 transition-colors" data-testid="photo-add-button">
          <Camera className="w-6 h-6" /> फोटो जोडा ({total}/5)
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="photo-file-input" />
    </div>
  );
}

function Success({ ad, reset }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center rise" data-testid="sell-success">
      <CheckCircle2 className="w-20 h-20 mx-auto text-green-600" />
      <h1 className="mt-4 font-display font-extrabold text-4xl text-[var(--navy)]" data-testid="sell-success-message">जाहिरात Live झाली!</h1>
      <p className="mt-2 text-slate-600">Your ad is now live across Maharashtra.</p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to={`/ad/${ad.id}`} className="rounded-full bg-[var(--buy)] text-white px-6 py-3 font-semibold" data-testid="sell-view-ad-link">जाहिरात पहा · View Ad</Link>
        <button onClick={reset} className="rounded-full border-2 border-[var(--orange)] text-[var(--navy)] px-6 py-3 font-semibold" data-testid="sell-post-another-button">आणखी जाहिरात टाका</button>
      </div>
    </div>
  );
}

const Label = ({ children, req }) => <label className="block text-sm font-semibold text-[var(--navy)] mb-1.5">{children}{req && <span className="text-[var(--sell)]"> *</span>}</label>;

function SellForm({ user }) {
  const [params] = useSearchParams();
  const preCat = CATEGORIES.includes(params.get("category")) ? params.get("category") : "";
  const initial = { ...EMPTY, category: preCat, mobile: user.mobile, location: user.district || "", taluka: user.taluka || "" };
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const editId = params.get("edit");
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isProperty = form.category === "Property";

  useEffect(() => {
    if (!editId) return;
    http.get(`/me/ads/${editId}`).then(({ data: a }) => {
      setForm({
        ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, a[k] ?? EMPTY[k]])),
        price: a.price ?? "", area: a.area ?? "", place: a.place || "", condition: a.condition || "",
        property_type: a.property_type || "", area_unit: a.area_unit || "",
      });
      setExisting(a.photos || []);
    }).catch(() => { toast.error("जाहिरात सापडली नाही"); nav("/account"); });
  }, [editId, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.mobile)) return toast.error("10 अंकी मोबाईल नंबर टाका");
    if (form.category === "Property" && existing.length + files.length < 2) return toast.error("प्रॉपर्टीसाठी किमान 2 फोटो आवश्यक आहेत");
    setBusy(true);
    try {
      const photos = [...existing];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        photos.push((await http.post("/upload", fd)).data.path);
      }
      const prop = form.category === "Property"
        ? { property_type: form.property_type || null, area: form.area === "" ? null : Number(form.area), area_unit: form.area_unit || null, has_712: form.has_712, direct_owner: form.direct_owner }
        : { property_type: null, area: null, area_unit: null, has_712: null, direct_owner: null };
      const payload = { ...form, ...prop, place: form.place.trim() || null, condition: NO_CONDITION.includes(form.category) ? null : form.condition || null, price: form.price === "" ? null : Number(form.price), photos };
      if (editId) {
        await http.put(`/me/ads/${editId}`, payload);
        toast.success("जाहिरात Update झाली!");
        nav("/account");
        return;
      }
      const { data } = await http.post("/ads", payload);
      toast.success("जाहिरात Live झाली!");
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success ad={done} reset={() => { setDone(null); setForm(initial); setFiles([]); }} />;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[var(--navy)]">{editId ? "जाहिरात Edit करा" : "जाहिरात टाका"} <span className="text-[var(--orange)]">· {editId ? "Edit Ad" : "Post Free Ad"}</span></h1>
      <p className="text-slate-600 mt-1">जाहिरात लगेच Live होईल · नो ब्रोकर, थेट खरेदीदार.</p>
      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-slate-200 p-5 md:p-8 space-y-6" data-testid="sell-form">
        <div>
          <Label req>श्रेणी · Category</Label>
          <select className="field" required value={form.category} onChange={set("category")} data-testid="sell-category-select">
            <option value="">श्रेणी निवडा · Select Category</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c} · {CATEGORY_INFO[c].mr}</option>)}
          </select>
        </div>
        {isProperty && <PropertyFields form={form} setForm={setForm} />}
        {form.category && !["Jobs", "Services", "Property"].includes(form.category) && (
          <div>
            <Label>बाजार · Condition</Label>
            <div className="grid grid-cols-2 gap-3">
              {[["old", "जुना बाजार · Used"], ["new", "नवीन बाजार · New"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, condition: form.condition === v ? "" : v })} className={`h-12 rounded-lg border-2 font-semibold transition-colors ${form.condition === v ? (v === "old" ? "border-[var(--orange)] bg-orange-50 text-[var(--navy)]" : "border-[var(--buy)] bg-blue-50 text-[var(--navy)]") : "border-slate-200 text-slate-600"}`} data-testid={`sell-condition-${v}`}>{l}</button>
              ))}
            </div>
          </div>
        )}
        <div>
          <Label req={isProperty}>फोटो · Photos (max 5){isProperty && " - प्रॉपर्टीसाठी किमान 2 फोटो"}</Label>
          <PhotoPicker files={files} setFiles={setFiles} existing={existing} setExisting={setExisting} />
        </div>
        <div>
          <Label req>शीर्षक · Title</Label>
          <input className="field" required minLength={3} maxLength={120} value={form.title} onChange={set("title")} placeholder="उदा. iPhone 13, 128GB, Good condition" data-testid="sell-title-input" />
        </div>
        <div>
          <Label req>वर्णन · Description</Label>
          <textarea className="field !h-32 py-2 resize-y" required minLength={5} maxLength={4000} value={form.description} onChange={set("description")} placeholder="वस्तूची माहिती, स्थिती, वापर..." data-testid="sell-description-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>किंमत · Price (₹)</Label>
            <input className="field" type="number" min={0} value={form.price} onChange={set("price")} placeholder="उदा. 15000" data-testid="sell-price-input" />
          </div>
          <div>
            <Label req>मोबाईल नंबर · Mobile</Label>
            <div className="flex">
              <span className="h-12 px-3 flex items-center rounded-l-lg border border-r-0 border-slate-300 bg-slate-50 text-slate-600">+91</span>
              <input className="field !rounded-l-none" required inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} placeholder="98XXXXXXXX" data-testid="sell-mobile-input" />
            </div>
          </div>
        </div>
        <div>
          <Label req>ठिकाण · Location (Maharashtra){isProperty && " - गावाचे नाव आवश्यक"}</Label>
          <LocationPicker required withPlace freePlace district={form.location} taluka={form.taluka} place={form.place} onChange={(location, taluka, place) => setForm({ ...form, location, taluka, place })} testPrefix="sell" />
        </div>
        <button type="submit" disabled={busy || (isProperty && form.direct_owner === false)} className="w-full h-14 rounded-xl bg-[var(--sell)] hover:bg-[#c62828] disabled:opacity-70 text-white font-display font-bold text-xl flex items-center justify-center gap-2 transition-colors" data-testid="sell-submit-button">
          {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</> : editId ? "बदल Save करा" : "जाहिरात Live करा"}
        </button>
      </form>
    </div>
  );
}

function Gate({ user }) {
  return (
    <div className="max-w-lg mx-auto px-4 py-14 text-center space-y-4" data-testid="sell-gate">
      <h1 className="font-display font-extrabold text-3xl text-[var(--navy)]">जाहिरात टाका</h1>
      {!user ? (
        <>
          <p className="text-slate-600" data-testid="sell-gate-login-message">जाहिरात टाकण्यासाठी आधी Login / नोंदणी करा. नवीन नोंदणीवर 3 दिवस Free Trial!</p>
          <Link to="/login?next=/sell" className="inline-flex h-12 px-6 items-center rounded-xl bg-[var(--sell)] text-white font-semibold" data-testid="sell-gate-login-button">Login / नोंदणी करा - मोफत</Link>
        </>
      ) : (
        <>
          <PlanStatus user={user} />
          <p className="text-slate-600">Membership घेऊन Unlimited जाहिराती टाका.</p>
          <Link to="/plans" className="inline-flex h-12 px-6 items-center rounded-xl bg-[var(--orange)] text-white font-semibold" data-testid="sell-gate-plans-button">Plans पहा</Link>
        </>
      )}
    </div>
  );
}

export default function Sell() {
  const { user } = useAuth();
  if (user === null) return <div className="py-20 text-center text-slate-500">Loading...</div>;
  return user && user.plan_state === "active" ? <SellForm user={user} /> : <Gate user={user} />;
}
